import {ApplicationRef, computed, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {countMatches, findMatches, foldQuery} from './profile-search-text';

/** Markiert Bereiche, die nicht zum Profilinhalt gehören (Navigation, Suchleiste, Export-Leiste). */
export const PROFILE_SEARCH_IGNORE_ATTR = 'data-profile-search-ignore';

const IGNORE_SELECTOR = [
  `[${PROFILE_SEARCH_IGNORE_ATTR}]`,
  'mat-icon',
  // Icons als Ligatur in einem schlichten Element, z. B. die Sterne der Fähigkeiten (<span class="material-icons">star</span>).
  '[class*="material-icons"]',
  '[class*="material-symbols"]',
  'button',
  'input',
  'textarea',
  'select',
  'script',
  'style',
  'mat-option',
  'lib-badge',
  'app-modal',
  'lib-modal',
  'app-twopager-save-dialog',
  '.mat-badge-content',
  '.cdk-visually-hidden',
  '.project__validation-badge',
].join(',');

const PANEL_SELECTOR = 'mat-expansion-panel';
const PANEL_HEADER_SELECTOR = ':scope > mat-expansion-panel-header';
const PANEL_EXPANDED_CLASS = 'mat-expanded';
const HIGHLIGHT_ALL = 'profile-search-hit';
const HIGHLIGHT_CURRENT = 'profile-search-current';
/** Etwas länger als die Auf-/Zuklapp-Animation der Material-Panels. */
const PANEL_ANIMATION_MS = 250;
const REFRESH_DEBOUNCE_MS = 150;

export interface ProfileSearchEntry {
  key: string;
  texts: string[];
}

/**
 * Bereich, der nur einen Teil seiner Einträge rendert (Projekte: nur das aktive).
 * Die übrigen Einträge liefern ihre Suchtexte aus dem Datenmodell und werden beim
 * Sprung über `activate` gerendert.
 */
export interface ProfileSearchSource {
  /** Id des Abschnitts-Ankers, zu dem die Treffer gehören. */
  readonly section: string;
  /** Element, an dessen Stelle der gerenderte Eintrag steht. */
  slot(): HTMLElement | null;
  activeKey(): string | null;
  /** Alle Einträge in Anzeigereihenfolge; der aktive wird über den DOM durchsucht. */
  entries(): ProfileSearchEntry[];
  activate(key: string | null): void;
}

export interface ProfileSearchConfig {
  root: HTMLElement;
  sectionAnchorSelector: string;
  sectionLabelKey: (anchorId: string) => string | null;
}

interface DomHit {
  kind: 'dom';
  range: Range;
  sectionKey: string | null;
}

interface VirtualHit {
  kind: 'virtual';
  source: ProfileSearchSource;
  key: string;
  occurrence: number;
  sectionKey: string | null;
}

export type ProfileSearchHit = DomHit | VirtualHit;

interface PanelChange {
  kind: 'panel';
  panel: HTMLElement;
  before: boolean;
  after: boolean;
}

interface SourceChange {
  kind: 'source';
  source: ProfileSearchSource;
  before: string | null;
  after: string | null;
}

type RevealChange = PanelChange | SourceChange;

function isExpanded(panel: HTMLElement): boolean {
  return panel.classList.contains(PANEL_EXPANDED_CLASS);
}

function panelHeader(panel: HTMLElement): HTMLElement | null {
  return panel.querySelector<HTMLElement>(PANEL_HEADER_SELECTOR);
}

function highlightRegistry(): HighlightRegistry | null {
  return typeof CSS !== 'undefined' && 'highlights' in CSS && typeof Highlight !== 'undefined'
    ? CSS.highlights
    : null;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * NP20-824: Suche über das Profil. Durchsucht wird der gerenderte Text — auch in
 * zugeklappten Panels, deren Inhalt im DOM liegt — plus die Suchtexte nicht
 * gerenderter Einträge aus registrierten Quellen. Markiert wird über die CSS Custom
 * Highlight API, der DOM bleibt dadurch unangetastet.
 */
@Injectable()
export class ProfileSearchService implements OnDestroy {
  private readonly appRef = inject(ApplicationRef);

  readonly isOpen = signal(false);
  readonly hits = signal<ProfileSearchHit[]>([]);
  readonly currentIndex = signal(-1);
  readonly total = computed(() => this.hits().length);
  readonly position = computed(() => this.currentIndex() + 1);
  readonly currentSectionKey = computed(() => this.hits()[this.currentIndex()]?.sectionKey ?? null);
  readonly queryActive = signal(false);

  private config: ProfileSearchConfig | null = null;
  private readonly sources: ProfileSearchSource[] = [];
  private foldedQuery = '';
  private changes: RevealChange[] = [];
  private navToken = 0;
  private observer: MutationObserver | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  configure(config: ProfileSearchConfig): void {
    this.config = config;
    this.observer?.disconnect();
    if (typeof MutationObserver === 'undefined') return;
    this.observer = new MutationObserver(records => {
      if (!this.foldedQuery) return;
      if (records.some(record => !this.isIgnored(record.target))) this.scheduleRefresh();
    });
    this.observer.observe(config.root, {childList: true, characterData: true, subtree: true});
  }

  registerSource(source: ProfileSearchSource): () => void {
    this.sources.push(source);
    return () => {
      const idx = this.sources.indexOf(source);
      if (idx >= 0) this.sources.splice(idx, 1);
    };
  }

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.clear();
    this.isOpen.set(false);
  }

  search(query: string): void {
    this.foldedQuery = foldQuery(query);
    this.queryActive.set(!!this.foldedQuery);
    if (!this.foldedQuery) {
      this.clear();
      return;
    }
    this.hits.set(this.collect());
    this.currentIndex.set(-1);
    if (this.total() > 0) {
      void this.goTo(0);
    } else {
      this.renderHighlights();
    }
  }

  next(): Promise<void> {
    const total = this.total();
    return total ? this.goTo((this.currentIndex() + 1) % total) : Promise.resolve();
  }

  previous(): Promise<void> {
    const total = this.total();
    const current = this.currentIndex();
    // Ohne aktuellen Treffer (-1) rückwärts auf den letzten, nicht auf den vorletzten.
    return total ? this.goTo(current < 0 ? total - 1 : (current - 1 + total) % total) : Promise.resolve();
  }

  async goTo(index: number): Promise<void> {
    const token = ++this.navToken;
    let hit = this.hits()[index];
    if (!hit) return;
    const previous = this.hits()[this.currentIndex()];
    const previousIndex = this.currentIndex();
    this.currentIndex.set(index);

    if (hit.kind === 'virtual') {
      const resolved = this.materialize(hit, index, previous, previousIndex);
      if (!resolved) {
        this.renderHighlights();
        return;
      }
      hit = resolved;
    }

    const revealed = this.revealPanels(hit.range.startContainer);
    this.renderHighlights();
    this.scrollTo(hit);
    if (revealed) {
      // Zuklappende Nachbar-Panels verschieben den Treffer noch während der Animation.
      await delay(PANEL_ANIMATION_MS);
      if (token === this.navToken) this.scrollTo(hit);
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.clearHighlights();
  }

  /** Alles zurücknehmen: Treffer, Markierungen und die von der Suche aufgeklappten Bereiche. */
  private clear(): void {
    this.navToken++;
    this.foldedQuery = '';
    this.queryActive.set(false);
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.restore();
    this.hits.set([]);
    this.currentIndex.set(-1);
    this.clearHighlights();
  }

  /** Rendert den Eintrag eines virtuellen Treffers und setzt auf den passenden DOM-Treffer um. */
  private materialize(
    hit: VirtualHit,
    index: number,
    previous: ProfileSearchHit | undefined,
    previousIndex: number,
  ): DomHit | null {
    const {source, key, occurrence} = hit;
    const before = source.activeKey();
    source.activate(key);
    this.flush();
    this.logChange({kind: 'source', source, before, after: source.activeKey()});

    const hits = this.collect();
    this.hits.set(hits);
    if (source.activeKey() !== key) {
      // Die Quelle hat abgelehnt (es wird gerade bearbeitet): neu gezählt ohne ihre
      // virtuellen Treffer, und der Zähler bleibt, wo er war.
      this.currentIndex.set(this.reselect(hits, previous, previousIndex));
      return null;
    }
    const slot = source.slot();
    const inside: number[] = [];
    hits.forEach((candidate, i) => {
      if (candidate.kind === 'dom' && slot?.contains(candidate.range.startContainer)) inside.push(i);
    });
    const next = inside.length
      ? inside[Math.min(occurrence, inside.length - 1)]
      : Math.min(index, hits.length - 1);
    this.currentIndex.set(next);
    const resolved = hits[next];
    return resolved?.kind === 'dom' ? resolved : null;
  }

  private collect(): ProfileSearchHit[] {
    const config = this.config;
    if (!config || !this.foldedQuery) return [];
    let hits: ProfileSearchHit[] = this.collectDomHits(config.root);
    for (const source of this.sources) {
      hits = this.mergeSource(hits, source);
    }
    const anchors = Array.from(config.root.querySelectorAll<HTMLElement>(config.sectionAnchorSelector));
    for (const hit of hits) {
      if (hit.kind === 'dom') hit.sectionKey = this.sectionOf(hit.range.startContainer, anchors);
    }
    return hits;
  }

  private collectDomHits(root: HTMLElement): DomHit[] {
    const doc = root.ownerDocument;
    const visibility = new Map<Element, boolean>();
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: node => this.isSearchable(node as Text, visibility)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
    });
    const hits: DomHit[] = [];
    for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
      for (const match of findMatches(node.data, this.foldedQuery)) {
        const range = doc.createRange();
        range.setStart(node, match.start);
        range.setEnd(node, match.end);
        hits.push({kind: 'dom', range, sectionKey: null});
      }
    }
    return hits;
  }

  private isSearchable(node: Text, visibility: Map<Element, boolean>): boolean {
    const parent = node.parentElement;
    if (!parent || !node.data.trim() || parent.closest(IGNORE_SELECTOR)) return false;
    let visible = visibility.get(parent);
    if (visible === undefined) {
      // Zugeklappte Panels sind nur `visibility: hidden` und zählen mit; `display: none` nicht.
      visible = parent.checkVisibility?.() ?? true;
      visibility.set(parent, visible);
    }
    return visible;
  }

  private mergeSource(hits: ProfileSearchHit[], source: ProfileSearchSource): ProfileSearchHit[] {
    const slot = source.slot();
    if (!slot) return hits;
    const before: ProfileSearchHit[] = [];
    const inside: ProfileSearchHit[] = [];
    const after: ProfileSearchHit[] = [];
    for (const hit of hits) {
      const node = this.anchorNode(hit);
      if (slot.contains(node)) inside.push(hit);
      else if (slot.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING) before.push(hit);
      else after.push(hit);
    }

    const activeKey = source.activeKey();
    const entries = source.entries();
    const sectionKey = this.config?.sectionLabelKey(source.section) ?? null;
    const middle: ProfileSearchHit[] = entries.some(entry => entry.key === activeKey) ? [] : inside;
    for (const entry of entries) {
      if (entry.key === activeKey) {
        middle.push(...inside);
        continue;
      }
      const count = countMatches(entry.texts, this.foldedQuery);
      for (let occurrence = 0; occurrence < count; occurrence++) {
        middle.push({kind: 'virtual', source, key: entry.key, occurrence, sectionKey});
      }
    }
    return [...before, ...middle, ...after];
  }

  private anchorNode(hit: ProfileSearchHit): Node {
    return hit.kind === 'dom' ? hit.range.startContainer : (hit.source.slot() ?? document.body);
  }

  private sectionOf(node: Node, anchors: HTMLElement[]): string | null {
    let current: HTMLElement | null = null;
    for (const anchor of anchors) {
      if (anchor.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) current = anchor;
      else break;
    }
    return current ? this.config?.sectionLabelKey(current.id) ?? null : null;
  }

  /** Klappt alle zugeklappten Panels um den Knoten auf und merkt sich, was sich dadurch geändert hat. */
  private revealPanels(node: Node): boolean {
    const collapsed: HTMLElement[] = [];
    for (let el = node.parentElement?.closest<HTMLElement>(PANEL_SELECTOR); el;
         el = el.parentElement?.closest<HTMLElement>(PANEL_SELECTOR)) {
      if (!isExpanded(el)) collapsed.unshift(el);
    }
    if (!collapsed.length) return false;

    const before = this.panelStates();
    for (const panel of collapsed) {
      panelHeader(panel)?.click();
      this.flush();
    }
    // Auch Panels, die ein Akkordeon dabei zugeklappt hat, müssen beim Schließen zurück.
    for (const [panel, wasExpanded] of before) {
      const nowExpanded = isExpanded(panel);
      if (nowExpanded !== wasExpanded) this.logChange({kind: 'panel', panel, before: wasExpanded, after: nowExpanded});
    }
    return true;
  }

  private panelStates(): Map<HTMLElement, boolean> {
    const states = new Map<HTMLElement, boolean>();
    this.config?.root.querySelectorAll<HTMLElement>(PANEL_SELECTOR).forEach(panel => states.set(panel, isExpanded(panel)));
    return states;
  }

  /** Pro Panel bzw. Quelle zählt der Zustand vor der ersten Änderung durch die Suche. */
  private logChange(change: RevealChange): void {
    if (change.kind === 'panel') {
      const existing = this.changes.find((c): c is PanelChange => c.kind === 'panel' && c.panel === change.panel);
      if (existing) existing.after = change.after;
      else this.changes.push(change);
    } else {
      const existing = this.changes.find((c): c is SourceChange => c.kind === 'source' && c.source === change.source);
      if (existing) existing.after = change.after;
      else this.changes.push(change);
    }
  }

  /**
   * Stellt den Zustand vor der Suche wieder her — nur dort, wo der Nutzer seitdem
   * nichts selbst umgeschaltet hat.
   */
  private restore(): void {
    const changes = this.changes;
    this.changes = [];
    for (const change of [...changes].reverse()) {
      if (change.before === change.after) continue;
      if (change.kind === 'panel') {
        if (change.panel.isConnected && isExpanded(change.panel) === change.after) {
          panelHeader(change.panel)?.click();
          this.flush();
        }
      } else if (change.source.activeKey() === change.after) {
        change.source.activate(change.before);
        this.flush();
      }
    }
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refresh();
    }, REFRESH_DEBOUNCE_MS);
  }

  /** Nach DOM-Änderungen (Speichern, Bearbeiten) neu zählen, ohne zu springen. */
  private refresh(): void {
    if (!this.foldedQuery) return;
    const previous = this.hits()[this.currentIndex()];
    const hits = this.collect();
    this.hits.set(hits);
    this.currentIndex.set(this.reselect(hits, previous, this.currentIndex()));
    this.renderHighlights();
  }

  /**
   * Derselbe Treffer wie zuvor, sonst der an der alten Position. Gibt es Treffer, ist
   * immer einer aktuell — auch wenn vorher keiner da war (0/0 → 1/N).
   */
  private reselect(hits: ProfileSearchHit[], previous: ProfileSearchHit | undefined, fallback: number): number {
    const same = previous ? hits.findIndex(hit => this.isSameHit(hit, previous)) : -1;
    if (same >= 0) return same;
    return hits.length ? Math.min(Math.max(fallback, 0), hits.length - 1) : -1;
  }

  private isSameHit(a: ProfileSearchHit, b: ProfileSearchHit): boolean {
    if (a.kind === 'dom' && b.kind === 'dom') {
      return a.range.startContainer === b.range.startContainer && a.range.startOffset === b.range.startOffset;
    }
    return a.kind === 'virtual' && b.kind === 'virtual'
      && a.source === b.source && a.key === b.key && a.occurrence === b.occurrence;
  }

  private isIgnored(node: Node): boolean {
    const el = node instanceof Element ? node : node.parentElement;
    return !!el?.closest(`[${PROFILE_SEARCH_IGNORE_ATTR}]`);
  }

  private scrollTo(hit: DomHit): void {
    hit.range.startContainer.parentElement?.scrollIntoView?.({block: 'center', inline: 'nearest'});
  }

  private renderHighlights(): void {
    const registry = highlightRegistry();
    if (!registry) return;
    const current = this.hits()[this.currentIndex()];
    const others = this.hits()
      .filter((hit): hit is DomHit => hit.kind === 'dom' && hit !== current)
      .map(hit => hit.range);
    registry.set(HIGHLIGHT_ALL, new Highlight(...others));
    if (current?.kind === 'dom') {
      const highlight = new Highlight(current.range);
      highlight.priority = 1;
      registry.set(HIGHLIGHT_CURRENT, highlight);
    } else {
      registry.delete(HIGHLIGHT_CURRENT);
    }
  }

  private clearHighlights(): void {
    const registry = highlightRegistry();
    registry?.delete(HIGHLIGHT_ALL);
    registry?.delete(HIGHLIGHT_CURRENT);
  }

  /** Macht Auf-/Zuklappen und Projektwechsel sofort im DOM sichtbar, damit weitergezählt werden kann. */
  private flush(): void {
    this.appRef.tick();
  }
}
